import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";

/**
 * AskNoraDrawer
 * A lightweight right-side drawer with quick suggestion prompts and an input.
 * Standalone to keep EventDetail clean and reusable elsewhere.
 */
export default function AskNoraDrawer({ open, onClose, onSend, event, suggestions }) {
  const [text, setText] = useState("");

  useEffect(() => {
    if (!open) setText("");
  }, [open]);

  const presetSuggestions = useMemo(() => (
    suggestions && suggestions.length ? suggestions : [
      "Which actions are at risk heading into the next check-in?",
      "Who needs follow-up this week and why?",
      "Summarize blockers mentioned across recent updates.",
      "What completion trends should I know about for this event?",
    ]
  ), [suggestions]);

  if (!open) return null;

  const handleSend = () => {
    if (!text.trim()) return;
    onSend && onSend(text.trim());
    setText("");
  };

  return createPortal(
    <>
      <DrawerOverlay onClick={onClose} />
      <DrawerPanel role="dialog" aria-modal="true" aria-label="Ask Nora">
        <DrawerHeader>
          <HeaderGroup>
            <Avatar aria-hidden />
            <div>
              <Title>Ask Nora</Title>
              <Subtitle>Ask Nora about action plans and check-ins.</Subtitle>
            </div>
          </HeaderGroup>
          <CloseButton type="button" onClick={onClose} aria-label="Close Ask Nora">×</CloseButton>
        </DrawerHeader>

        <DrawerBody>
          <Helper>
            Example: "Which actions are at risk heading into Check-in #3?"
          </Helper>

          <Suggestions>
            {presetSuggestions.map((s, i) => (
              <Suggestion key={i} type="button" onClick={() => setText(s)}>{s}</Suggestion>
            ))}
          </Suggestions>
        </DrawerBody>

        <DrawerFooter>
          <Input
            placeholder="Ask Nora about action plans..."
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSend(); }}
            aria-label="Ask Nora input"
          />
          <SendButton type="button" onClick={handleSend} disabled={!text.trim()}>Send</SendButton>
        </DrawerFooter>
      </DrawerPanel>
    </>,
    document.body
  );
}

// Styles
const OverlayFade = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const SlideIn = keyframes`
  from { transform: translateX(100%); }
  to { transform: translateX(0%); }
`;

const DrawerOverlay = styled.div`
  position: fixed; inset: 0; background: rgba(17,24,39,0.45); z-index: 1000;
  animation: ${OverlayFade} 200ms ease;
`;

const DrawerPanel = styled.aside`
  position: fixed; top: 0; right: 0; height: 100%; width: min(520px, 100vw);
  background: #fff; box-shadow: -2px 0 12px rgba(0,0,0,0.12); z-index: 1001;
  display: grid; grid-template-rows: auto 1fr auto; animation: ${SlideIn} 240ms ease;
  border-radius: 8px 0 0 8px;
`;

const DrawerHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between; padding: 16px 18px; border-bottom: 1px solid #e5e7eb;
`;

const HeaderGroup = styled.div`
  display: flex; align-items: center; gap: 10px;
`;

const Avatar = styled.div`
  width: 36px; height: 36px; border-radius: 999px; background: linear-gradient(135deg, #2c6bed, #6b5cff);
`;

const Title = styled.div`
  font-weight: 700; color: #111827;
`;

const Subtitle = styled.div`
  font-size: 12px; color: #6b7280; margin-top: 2px;
`;

const CloseButton = styled.button`
  border: 1px solid #e5e7eb; background: #fff; color: #111827; width: 32px; height: 32px; border-radius: 8px; cursor: pointer; font-size: 18px; line-height: 1;
`;

const DrawerBody = styled.div`
  padding: 16px 18px; overflow: auto; display: grid; gap: 12px;
`;

const Helper = styled.div`
  font-size: 13px; color: #6b7280; background: #f9fafb; border: 1px solid #f3f4f6; border-radius: 8px; padding: 10px 12px;
`;

const Suggestions = styled.div`
  display: grid; gap: 8px;
`;

const Suggestion = styled.button`
  text-align: left; padding: 10px 12px; border-radius: 10px; border: 1px solid #e5e7eb; background: #fff; color: #111827; font-size: 13px; cursor: pointer; font-weight: 600;
  &:hover { background: #f9fafb; }
`;

const DrawerFooter = styled.div`
  display: grid; grid-template-columns: 1fr auto; gap: 8px; padding: 12px 18px; border-top: 1px solid #e5e7eb;
`;

const Input = styled.input`
  height: 38px; padding: 0 12px; border: 1px solid #e5e7eb; border-radius: 10px; font-size: 14px;
`;

const SendButton = styled.button`
  height: 38px; padding: 0 14px; border-radius: 10px; border: 1px solid #2c6bed; background: linear-gradient(90deg, #2c6bed, #6b5cff); color: #fff; font-weight: 700; font-size: 13px; cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

